/**
 * PresenceAvatars — Google-Slides-style presence indicator.
 *
 * Clean, non-overlapping avatars with online dot inside the ring.
 *  - Online  → full-colour photo (or initials) + green dot
 *  - Offline → greyscale + reduced opacity, no green dot
 */

import type { TeamMember } from '../hooks/useGlobalPresence'
import { storageImageUrl } from '../lib/storageImage'

interface Props {
  team: TeamMember[]
  currentUserId: string | undefined
  /** Max avatars before "+N" counter (default 5) */
  max?: number
}

export default function PresenceAvatars({ team, currentUserId, max = 5 }: Props) {
  // Sort: self first, then online, then offline
  const sorted = [...team]
    .sort((a, b) => {
      if (a.id === currentUserId) return -1
      if (b.id === currentUserId) return 1
      if (a.online === b.online) return 0
      return a.online ? -1 : 1
    })

  if (sorted.length === 0) return null

  const visible = sorted.slice(0, max)
  const overflow = sorted.length - max

  return (
    <div className="flex items-center gap-1.5">
      {visible.map(member => (
        <div
          key={member.id}
          className="relative group flex-shrink-0"
        >
          {/* Avatar circle */}
          <div
            className={`w-8 h-8 rounded-full ring-2 ring-white flex items-center justify-center text-caption font-semibold overflow-hidden transition-all duration-200 ${
              member.online ? '' : 'grayscale opacity-40'
            }`}
          >
            {member.avatarUrl ? (
              <img
                src={storageImageUrl(member.avatarUrl, { width: 100 })}
                alt={member.fullName}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-full h-full bg-primary/10 text-primary flex items-center justify-center">
                {initials(member.fullName)}
              </div>
            )}
          </div>

          {/* Green online dot — bottom-right, inside the ring */}
          {member.online && (
            <span className="absolute bottom-0 right-0 block h-2 w-2 rounded-full bg-success ring-[1.5px] ring-white" />
          )}

          {/* Tooltip on hover */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-ink text-white text-caption whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
            {member.fullName}
            {!member.online && <span className="text-ink-muted ml-1">(hors ligne)</span>}
          </div>
        </div>
      ))}

      {/* Overflow counter */}
      {overflow > 0 && (
        <span className="text-caption font-medium text-ink-muted">
          +{overflow}
        </span>
      )}
    </div>
  )
}

function initials(name: string): string {
  return name
    .split(' ')
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}
